import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo } from 'react';
import useGroupSettingsEffects from '../../hooks/context/effects/useGroupSettingsEffects';
import useGroupSettingsContext from '../../hooks/context/useGroupSettingsContext';
import { InvestmentThesis } from '../../types/files';
import { AuthContext } from '../AuthContext';
import storage from '../../utils/storage';

type GroupSettingsContextProps = {
  settingsLoaded: boolean;
  group: string;
  statuses: string[];
  setStatuses: Dispatch<SetStateAction<string[]>>;
  tags: string[];
  setTags: Dispatch<SetStateAction<string[]>>;
  stages: string[];
  setStages: Dispatch<SetStateAction<string[]>>;
  externalSync: boolean;
  setExternalSync: Dispatch<SetStateAction<boolean>>;
  autoIngestFromEmail: boolean;
  setAutoIngestFromEmail: Dispatch<SetStateAction<boolean>>;
  autoSendDigestEmail: boolean;
  setAutoSendDigestEmail: Dispatch<SetStateAction<boolean>>;
  investmentThesis?: InvestmentThesis|null;
  setInvestmentThesis: Dispatch<SetStateAction<InvestmentThesis|any>>;
  excludedCompanyTerms: string[];
  setExcludedCompanyTerms: Dispatch<SetStateAction<string[]>>;
  refreshSettings: () => void;
};

export const GroupSettingsContext = createContext<GroupSettingsContextProps>({
  settingsLoaded: false,
  group: '',
  statuses: [],
  setStatuses: () => {},
  tags: [],
  setTags: () => {},
  stages: [],
  setStages: () => {},
  externalSync: false,
  setExternalSync: () => {},
  autoIngestFromEmail: false,
  setAutoIngestFromEmail: () => {},
  autoSendDigestEmail: false,
  setAutoSendDigestEmail: () => {},
  investmentThesis: null,
  setInvestmentThesis: () => {},
  excludedCompanyTerms: [],
  setExcludedCompanyTerms: () => {},
  refreshSettings: () => {},
});

const GroupSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userGroup } = useContext(AuthContext);
  const {
    settingsLoaded,
    group,
    statuses,
    tags,
    stages,
    externalSync,
    autoIngestFromEmail,
    autoSendDigestEmail,
    investmentThesis,
    excludedCompanyTerms,
    setStatuses,
    setTags,
    setStages,
    setExternalSync,
    setAutoIngestFromEmail,
    setAutoSendDigestEmail,
    setInvestmentThesis,
    setExcludedCompanyTerms,
  } = useGroupSettingsContext();
  const { refreshSettings } = useGroupSettingsEffects(userGroup);

  const contextValue = useMemo(() => ({
    settingsLoaded,
    group,
    statuses,
    setStatuses,
    tags,
    setTags,
    stages,
    setStages,
    externalSync,
    setExternalSync,
    autoIngestFromEmail,
    setAutoIngestFromEmail,
    autoSendDigestEmail,
    setAutoSendDigestEmail,
    investmentThesis,
    setInvestmentThesis,
    excludedCompanyTerms,
    setExcludedCompanyTerms,
    refreshSettings
  }), [
    settingsLoaded,
    group,
    statuses,
    setStatuses,
    tags,
    setTags,
    stages,
    setStages,
    externalSync,
    setExternalSync,
    autoIngestFromEmail,
    setAutoIngestFromEmail,
    autoSendDigestEmail,
    setAutoSendDigestEmail,
    investmentThesis,
    setInvestmentThesis,
    excludedCompanyTerms,
    setExcludedCompanyTerms,
    refreshSettings,
  ]);

  if (storage.getItem('config.logger-context'))
    console.log("CTX", ["GroupSettings", contextValue]);

  return (
    <GroupSettingsContext.Provider value={contextValue}>
      {children}
    </GroupSettingsContext.Provider>
  );
};

export default GroupSettingsProvider;
